import {
  JOURNEY_ACTIVE_MACROS,
  JOURNEY_FUTURE_MACROS,
  getJourneyMacroIdForEtapa,
  type JourneyMacroId,
  type JourneyMacroStage,
} from '@/components/Deals/dealsJourney.constants'
import type { DealStage } from '@/types'
import type { DashboardData } from '@/hooks/dashboardDataTypes'

export type JourneyMacroAggregate = {
  macro: JourneyMacroStage
  count: number
  value: number
  pct: number
}

export type OperacaoSummaryView = {
  pipelineValue: number
  pipelineCount: number
  wonRevenue: number
  wonDeals: number
  lostDeals: number
  winRate: number
  closedCount: number
  dealsByStage: { stage: string; count: number; value: number }[]
  dealsCreatedByMonth: { month: string; count: number; value: number }[]
}

function prismaStageToDealStage(stage: string): DealStage | null {
  const key = stage.trim().toLowerCase() as DealStage
  const macroId = getJourneyMacroIdForEtapa(key)
  return macroId ? key : null
}

export function groupDealsByJourneyMacro(
  dealsByStage: { stage: string; count: number; value: number }[],
): JourneyMacroAggregate[] {
  const buckets = new Map<JourneyMacroId, { count: number; value: number }>()
  for (const macro of JOURNEY_ACTIVE_MACROS) {
    buckets.set(macro.id, { count: 0, value: 0 })
  }

  for (const row of dealsByStage) {
    const etapa = prismaStageToDealStage(row.stage)
    if (!etapa) continue
    const macroId = getJourneyMacroIdForEtapa(etapa)
    if (!macroId || !buckets.has(macroId)) continue
    const bucket = buckets.get(macroId)!
    bucket.count += row.count
    bucket.value += row.value
  }

  const total = [...buckets.values()].reduce((sum, b) => sum + b.count, 0)

  return JOURNEY_ACTIVE_MACROS.map((macro) => {
    const bucket = buckets.get(macro.id) ?? { count: 0, value: 0 }
    return {
      macro,
      count: bucket.count,
      value: bucket.value,
      pct: total > 0 ? Math.round((bucket.count / total) * 100) : 0,
    }
  })
}

export function countFuturePipelineDeals(
  dealsByStage: { stage: string; count: number; value: number }[],
): { pausado: number; fechamentoFuturo: number } {
  let pausado = 0
  let fechamentoFuturo = 0

  for (const row of dealsByStage) {
    const etapa = prismaStageToDealStage(row.stage)
    if (!etapa) continue
    if (etapa === 'pausado') pausado += row.count
    if (etapa === 'fechamento_futuro') fechamentoFuturo += row.count
  }

  return { pausado, fechamentoFuturo }
}

export function mapOperacaoSummaryData(data: DashboardData): OperacaoSummaryView | null {
  if (!data.summary) return null
  const wonDeals = data.summary.dealsFechadosMes
  const lostDeals = data.summary.lostDeals
  return {
    pipelineValue: data.summary.pipelineValue,
    pipelineCount: data.summary.pipelineCount,
    wonRevenue: data.summary.receitaMensal,
    wonDeals,
    lostDeals,
    winRate: data.summary.taxaConversao,
    closedCount: wonDeals + lostDeals,
    dealsByStage: data.summary.dealsByStage,
    dealsCreatedByMonth: [],
  }
}

export function formatWinRateDisplay(
  winRate: number,
  closedCount: number,
  valuesVisible: boolean,
): { value: string; hint?: string } {
  if (closedCount === 0) {
    return { value: '—', hint: 'Sem fechamentos no período' }
  }
  const formatted = valuesVisible ? `${winRate.toFixed(1)}%` : '•••'
  if (closedCount < 3) {
    return { value: formatted, hint: 'Amostra pequena' }
  }
  return { value: formatted }
}

/** Chips laterais (pausado / fechamento futuro) para contexto rápido. */
export function getOperacaoSideChips(dealsByStage: OperacaoSummaryView['dealsByStage']) {
  const future = countFuturePipelineDeals(dealsByStage)
  const chips: { label: string; count: number; color: string }[] = []

  for (const macro of JOURNEY_FUTURE_MACROS) {
    const count = macro.id === 'pausado' ? future.pausado : future.fechamentoFuturo
    if (count > 0) {
      chips.push({ label: macro.label, count, color: macro.color })
    }
  }

  return chips
}
